import { Assets, Texture } from 'pixi.js';

import riverVariedUrl from '../../../assets/game/reference/sk/river_example_meander_varied.png?inline';

export interface GameAssets {
  readonly tiles: Record<string, Texture>;
  readonly buildings: Record<string, Texture>;
  readonly units: Record<string, Texture>;
  readonly environment: Record<string, Texture>;
  readonly reference: Record<string, Texture>;
}

const tileKeys = [
  'settlement_meadow_light',
  'settlement_meadow_dark',
  'top_grass',
  'top_forest',
  'top_field',
  'top_water',
  'top_shore',
  'flowers_white',
  'flowers_blue',
  'grass_patch_light',
  'grass_patch_dark',
  'settlement_workyard',
  'settlement_road_h',
  'settlement_road_v',
  'settlement_road_diag_up',
  'settlement_road_diag_down',
  'settlement_road_junction',
  'field',
  'stone',
] as const;

const buildingKeys = [
  'castle',
  'warehouse',
  'market',
  'house',
  'farm',
  'woodcutter_camp',
  'mine',
  'quarry',
  'mill',
  'bakery',
  'smithy',
  'watchtower',
] as const;

const unitKeys = ['worker', 'woodcutter', 'farmer', 'miner', 'knight', 'trader'] as const;
const environmentKeys = ['tree_oak', 'tree_pine', 'bridge_h', 'bridge_v'] as const;
const ASSET_CACHE_VERSION = '2026-08-03-visible-farm-v2';
const baseReferenceKeys = [
  'voxel/castle/stage_1',
  'voxel/castle/stage_2',
  'voxel/castle/stage_3',
  'voxel/castle/stage_4',
  'voxel/castle/stage_5',
  'voxel/castle/stage_6',
  'events/merchant_caravan_v2',
  'events/dragon_attack_pixel_v1',
  'events/viking_raid_pixel_v1',
  'events/merchant_caravan_pixel_v3',
  'events/treasure_found_pixel_v1',
  'terrain/grass_plain',
  'terrain/grass_flowers',
  'terrain/grass_meadow',
  'terrain/grass_weeds',
  'terrain/grass_rocky',
  'terrain/dirt',
  'terrain/stone_transition',
  'roads/dirt_v',
  'roads/dirt_corner',
  'roads/dirt_t',
  'roads/dirt_cross',
  'roads/dirt_end',
  'roads/stone_v',
  'roads/stone_corner',
  'roads/stone_t',
  'roads/stone_cross',
  'water/deep',
  'water/shallow',
  'water/shore_north',
  'water/shore_south',
  'water/river_v',
  'water/river_bend',
  'water/river_cross',
  'water/waterfall',
  'water/rapids',
  'water/reeds',
  'environment/pine_single',
  'environment/oak_single',
  'environment/pine_cluster',
  'environment/mixed_forest',
  'environment/rocks_small',
  'environment/rocks_large',
  'environment/mountain_01',
  'environment/mountain_02',
  'fields/wheat',
  'fields/plowed',
  'fields/vegetables',
  'fields/harvested',
  'decor/flowers_white',
  'decor/flowers_blue',
  'decor/stump',
  'decor/log',
  'decor/mushrooms',
  'decor/campfire',
  'decor/fence_h',
  'decor/fence_corner',
  'classic/grass_plain',
  'classic/road_h',
  'classic/road_v',
  'classic/road_cross',
  'classic/stone_h',
  'classic/water_deep',
  'classic/river_v',
  'classic/river_h',
  'water27/sea_deep_1',
  'water27/sea_deep_2',
  'water27/sea_deep_3',
  'water27/shallow_1',
  'water27/shallow_2',
  'water27/shore_north',
  'water27/shore_south',
  'water27/shore_east',
  'water27/shore_west',
  'water27/shore_in_ne',
  'water27/shore_in_nw',
  'water27/shore_in_se',
  'water27/shore_in_sw',
  'water27/shore_out_ne',
  'water27/shore_out_nw',
  'water27/shore_out_se',
  'water27/shore_out_sw',
  'water27/island_center',
  'water27/peninsula_n',
  'water27/bay_ne',
  'water27/river_h',
  'water27/river_v',
  'water27/river_bend_ne',
  'water27/river_bend_nw',
  'water27/river_bend_se',
  'water27/river_bend_sw',
  'water27/river_t_n',
  'water27/river_t_s',
  'water27/river_t_e',
  'water27/river_t_w',
  'water27/river_cross',
  'water27/river_source',
  'water27/river_mouth',
  'water27/river_delta_1',
  'water27/rapids_1',
  'water27/waterfall_top',
  'water27/waterfall_mid',
  'water27/waterfall_bottom',
  'water27/bridge_wood_h',
  'water27/bridge_wood_v',
  'water27/reeds_shore',
  'water27/fishing_spot',
  'expansion/bridge_wood_h',
  'expansion/bridge_wood_v',
  'expansion/bridge_stone_h',
  'expansion/bridge_stone_v',
  'expansion/waterfall_mid',
  'expansion/mountain_2x2_01',
  'expansion/mountain_3x3_01',
  'buildingref/house',
  'buildingref/house_padded',
  'buildingref/woodcutter_camp',
  'buildingref/woodcutter_camp_padded',
  'buildingref/quarry',
  'buildingref/quarry_padded',
  'buildingref/mine',
  'buildingref/mine_padded',
  'buildingref/farm',
  'buildingref/farm_padded',
  'buildingref/mill',
  'buildingref/mill_padded',
  'buildingref/bakery',
  'buildingref/bakery_padded',
  'buildingref/smithy',
  'buildingref/smithy_padded',
  'buildingref/barracks',
  'buildingref/watchtower',
  'buildingref/watchtower_padded',
  'buildingref/market',
  'buildingref/market_padded',
  'buildingref/castle',
  'buildingref/castle_padded',
  'buildingref/warehouse',
  'buildingref/warehouse_padded',
  'buildingref/mage_tower',
  'buildingref/stable',
  'buildingref/harbor',
  'buildingref/lighthouse',
  'buildingref/iron_mine',
  'buildingref/iron_mine_padded',
  'buildingref/clay_pit',
  'buildingref/clay_pit_padded',
  'buildingref/smelter',
  'buildingref/smelter_padded',
  'buildingref/spy_office',
  'buildingref/spy_office_padded',
  'buildingref/brickyard',
  'buildingref/brickyard_padded',
  'buildingref/builder_yard',
  'buildingref/builder_yard_padded',
  'buildingref/wall',
  'buildingref/wall_padded',
  'buildingref/wall_straight_nw',
  'buildingref/wall_straight_ne',
  'buildingref/wall_straight_vertical',
  'buildingref/wall_straight_horizontal',
  'buildingref/wall_corner',
  'buildingref/wall_tower',
  'buildingref/wall_gate',
  'buildingref/siege_workshop',
  'buildingref/siege_workshop_padded',
  'props/tree_pine',
  'props/tree_pine_clean',
  'props/tree_pine_alt',
  'props/tree_pine_alt_clean',
  'props/tree_oak',
  'props/tree_oak_clean',
  'props/dead_tree',
  'props/rocks',
  'props/bush',
  'props/fence_h',
  'props/crate',
  'props/barrel',
  'props/banner_blue',
  'sk/road_h',
  'sk/road_v',
  'sk/road_corner',
  'sk/road_t',
  'sk/road_cross',
  'sk/road_end',
  'sk/road_h_overlay',
  'sk/road_v_overlay',
  'sk/road_corner_overlay',
  'sk/road_t_overlay',
  'sk/road_cross_overlay',
  'sk/road_end_overlay',
  'sk/river_h',
  'sk/river_v',
  'sk/river_bend',
  'sk/river_t',
  'sk/river_cross',
  'sk/river_example_meander',
  'sk/river_example_meander_cutout',
  'sk/river_example_meander_polished',
	'sk/river_example_meander_varied',
  'sk/lake_example_polished',
  'sk/lake_example_polished_cutout',
  'sk/lake_river_style_v2',
  'sk/lake_example_ref_small',
  'sk/lake_example_ref_small_cutout',
  'sk/lake_example_ref_medium',
  'sk/lake_example_ref_island',
  'sk/lake_example_ref_island_cutout',
  'sk/water27_river_h_clean',
  'sk/water27_river_v_clean',
  'sk/water27_river_bend_ne_clean',
  'sk/water27_river_bend_nw_clean',
  'sk/water27_river_bend_se_clean',
  'sk/water27_river_bend_sw_clean',
  'sk/water27_river_t_n_clean',
  'sk/water27_river_t_s_clean',
  'sk/water27_river_t_e_clean',
  'sk/water27_river_t_w_clean',
  'sk/water27_river_cross_clean',
  'sk/water27_river_source_clean',
  'sk/water27_river_source_cutout',
  'sk/river_source_pool_polished',
  'sk/river_source_spring_visible',
  'sk/river_source_spring_matched_v2',
  'sk/water27_river_mouth_clean',
  'sk/water27_river_delta_1_clean',
  'sk/water27_sea_deep_1_clean',
  'sk/water27_sea_deep_2_clean',
  'sk/water27_sea_deep_3_clean',
  'sk/water27_shallow_1_clean',
  'sk/water27_shallow_2_clean',
  'sk/water27_shore_north_clean',
  'sk/water27_shore_south_clean',
  'sk/water27_shore_east_clean',
  'sk/water27_shore_west_clean',
  'sk/water27_shore_out_ne_clean',
  'sk/water27_shore_out_nw_clean',
  'sk/water27_shore_out_se_clean',
  'sk/water27_shore_out_sw_clean',
  'sk/water27_shore_in_ne_clean',
  'sk/water27_shore_in_nw_clean',
  'sk/water27_shore_in_se_clean',
  'sk/water27_shore_in_sw_clean',
  'sk/water27_bridge_wood_h_clean',
  'sk/water27_bridge_wood_v_clean',
  'sk/bridge_h_clean',
  'sk/bridge_v_clean',
  'sk/bridge_h_clean_cutout',
  'sk/bridge_h_sharp_v2',
  'sk/bridge_v_clean_cutout',
  'sk/lake_deep',
  'sk/lake_shore',
  'sk/lake_corner',
  'sk/lake_example_small',
  'sk/bridge_h',
  'sk/bridge_v',
  'sk/grass_plain_clean',
  'sk/grass_flowers_clean',
  'sk/grass_meadow_clean',
  'sk/grass_weeds_clean',
  'sk/road_h_overlay_clean',
  'sk/road_v_overlay_clean',
  'sk/road_corner_overlay_clean',
  'sk/road_t_overlay_clean',
  'sk/road_cross_overlay_clean',
  'sk/road_end_overlay_clean',
  'sk/road_composite_polished',
  'seasonal/spring/grass',
  'seasonal/spring/field',
  'seasonal/spring/lake_deep',
  'seasonal/spring/lake_shore',
  'seasonal/spring/river_h',
  'seasonal/spring/river_v',
  'seasonal/spring/river_bend',
  'seasonal/spring/road_h_overlay',
  'seasonal/spring/road_v_overlay',
  'seasonal/spring/road_corner_overlay',
  'seasonal/spring/road_t_overlay',
  'seasonal/spring/road_cross_overlay',
  'seasonal/spring/road_end_overlay',
  'seasonal/summer/grass',
  'seasonal/summer/field',
  'seasonal/summer/lake_deep',
  'seasonal/summer/lake_shore',
  'seasonal/summer/river_h',
  'seasonal/summer/river_v',
  'seasonal/summer/river_bend',
  'seasonal/summer/road_h_overlay',
  'seasonal/summer/road_v_overlay',
  'seasonal/summer/road_corner_overlay',
  'seasonal/summer/road_t_overlay',
  'seasonal/summer/road_cross_overlay',
  'seasonal/summer/road_end_overlay',
  'seasonal/autumn/grass',
  'seasonal/autumn/field',
  'seasonal/autumn/lake_deep',
  'seasonal/autumn/lake_shore',
  'seasonal/autumn/river_h',
  'seasonal/autumn/river_v',
  'seasonal/autumn/river_bend',
  'seasonal/autumn/road_h_overlay',
  'seasonal/autumn/road_v_overlay',
  'seasonal/autumn/road_corner_overlay',
  'seasonal/autumn/road_t_overlay',
  'seasonal/autumn/road_cross_overlay',
  'seasonal/autumn/road_end_overlay',
  'seasonal/winter/grass',
  'seasonal/winter/field',
  'seasonal/winter/lake_deep',
  'seasonal/winter/lake_shore',
  'seasonal/winter/river_h',
  'seasonal/winter/river_v',
  'seasonal/winter/river_bend',
  'seasonal/winter/road_h_overlay',
  'seasonal/winter/road_v_overlay',
  'seasonal/winter/road_corner_overlay',
  'seasonal/winter/road_t_overlay',
  'seasonal/winter/road_cross_overlay',
  'seasonal/winter/road_end_overlay',
  'building_layers/spring_ground',
  'building_layers/summer_ground',
  'building_layers/autumn_ground',
  'building_layers/winter_ground',
  'building_layers/spring_overlay',
  'building_layers/summer_overlay',
  'building_layers/autumn_overlay',
  'building_layers/winter_overlay',
] as const;

const winterCastleKeys = [
  'voxel/castle/stage_1_winter',
  'voxel/castle/stage_2_winter',
  'voxel/castle/stage_3_winter',
  'voxel/castle/stage_4_winter',
  'voxel/castle/stage_5_winter',
  'voxel/castle/stage_6_winter',
] as const;
const winterBuildingKeys = baseReferenceKeys
  .filter((key) => key.startsWith('buildingref/'))
  .map((key) => `${key}_winter`);
const referenceKeys: readonly string[] = [
  ...baseReferenceKeys,
  ...winterCastleKeys,
  ...winterBuildingKeys,
];

function gameAssetBase(): string {
  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (currentScript?.src) {
    if (currentScript.src.includes('/src/')) {
      return new URL('/assets/game/', window.location.href).toString();
    }

    return new URL('../game/', currentScript.src).toString();
  }

  return new URL('/assets/game/', window.location.href).toString();
}

export async function loadGameAssets(): Promise<GameAssets> {
  const base = gameAssetBase();
  const manifest: Record<string, string> = {};

  for (const key of tileKeys) manifest[`tile:${key}`] = versioned(`${base}tiles/${key}.png`);
  for (const key of buildingKeys) manifest[`building:${key}`] = versioned(`${base}buildings/${key}.png`);
  for (const key of unitKeys) manifest[`unit:${key}`] = versioned(`${base}characters/male/${key}.png`);
  for (const key of environmentKeys) manifest[`environment:${key}`] = versioned(`${base}environment/${key}.png`);
  for (const key of referenceKeys) {
	manifest[`reference:${key}`] = key === 'sk/river_example_meander_varied' ? riverVariedUrl : versioned(`${base}reference/${key}.png`);
	}

  const loaded = await Assets.load(
    Object.entries(manifest).map(([alias, src]) => ({
      alias,
      src,
    })),
  );

  // The source artwork is intentionally crisp, small-scale game art. Pixi's
  // default linear filtering softens it whenever buildings or the large water
  // composites are scaled. Nearest sampling plus disabled mipmaps preserves
  // the authored edges in OBS and in the responsive browser preview.
  for (const [alias, texture] of Object.entries(loaded)) {
    if (
      alias.startsWith('reference:buildingref/') ||
      alias.startsWith('reference:voxel/castle/') ||
      alias === 'reference:sk/river_example_meander_varied' ||
      alias === 'reference:sk/lake_example_polished_cutout' ||
      alias === 'reference:sk/lake_river_style_v2' ||
      alias === 'reference:sk/river_source_spring_visible' ||
      alias === 'reference:sk/river_source_spring_matched_v2'
    ) {
      const source = (texture as Texture).source;
      source.scaleMode = 'nearest';
      source.autoGenerateMipmaps = false;
    }
  }

  return {
    tiles: Object.fromEntries(tileKeys.map((key) => [key, loaded[`tile:${key}`] as Texture])),
    buildings: Object.fromEntries(buildingKeys.map((key) => [key, loaded[`building:${key}`] as Texture])),
    units: Object.fromEntries(unitKeys.map((key) => [key, loaded[`unit:${key}`] as Texture])),
    environment: Object.fromEntries(environmentKeys.map((key) => [key, loaded[`environment:${key}`] as Texture])),
    reference: Object.fromEntries(referenceKeys.map((key) => [key, loaded[`reference:${key}`] as Texture])),
  };
}

function versioned(src: string): string {
  return `${src}?v=${ASSET_CACHE_VERSION}`;
}
